// Mortgage amortization — pure, deterministic, cent-perfect.
//
// The math here is the trust surface of the whole product. It is intentionally
// framework-free (no DOM, no I/O, no Date, no randomness) so it can be exercised
// under Vitest, executed in the browser bundle, or later reused in a Worker.
//
// Formula: standard fixed-rate mortgage payment
//   M = P * i(1+i)^n / ((1+i)^n - 1)
// where P = loan principal, i = monthly interest rate, n = number of monthly
// payments. Each month's interest is computed on the outstanding balance and
// rounded to cents; principal is the remainder; the final month absorbs any
// rounding drift so the balance closes to exactly $0.00. This matches the
// per-cent output of the amortization tables published by Bankrate,
// Nerdwallet, and Wikipedia's Amortization schedule article.

/**
 * Citations for every default surfaced to the user. Any default value shown
 * in the UI must cite one of these entries (in a tooltip or the methodology
 * page). The `name` key here matches the key in `MORTGAGE_DEFAULTS`.
 */
export const DEFAULT_SOURCES = {
  annualInterestRatePercent: {
    label: "Freddie Mac PMMS — 30-year fixed rate average",
    url: "https://www.freddiemac.com/pmms",
    note: "Weekly Primary Mortgage Market Survey; national average for the 30-year fixed conventional loan.",
  },
  termYears: {
    label: "Freddie Mac PMMS — anchor product",
    url: "https://www.freddiemac.com/pmms",
    note: "30-year fixed is the most common US mortgage term and Freddie Mac's headline PMMS series.",
  },
  downPaymentPercent: {
    label: "NAR Profile of Home Buyers and Sellers",
    url: "https://www.nar.realtor/research-and-statistics/research-reports/highlights-from-the-profile-of-home-buyers-and-sellers",
    note: "Median down payment for repeat buyers; also the conventional threshold to avoid private mortgage insurance.",
  },
} as const;

/**
 * User-facing defaults for the mortgage inputs. Every value here must have a
 * matching entry in `DEFAULT_SOURCES` and a visible citation in the UI.
 */
export const MORTGAGE_DEFAULTS = {
  annualInterestRatePercent: 6.5,
  termYears: 30,
  downPaymentPercent: 20,
} as const;

const MONTHS_PER_YEAR = 12;
const PERCENT_DIVISOR = 100;
const CENTS_PER_DOLLAR = 100;

export type MortgageInputs = {
  /** Home purchase price in dollars. */
  homePrice: number;
  /** Down payment in dollars. */
  downPayment: number;
  /** Nominal annual interest rate as a percentage (e.g. 6.5 for 6.5%). */
  annualInterestRatePercent: number;
  /** Loan term in whole years. */
  termYears: number;
};

export type AmortizationMonth = {
  /** 1-indexed month number. */
  month: number;
  /** Portion of the payment applied to interest (dollars, 2dp). */
  interest: number;
  /** Portion of the payment applied to principal (dollars, 2dp). */
  principal: number;
  /** Balance remaining after this month's payment (dollars, 2dp). */
  balance: number;
};

export type MortgageSchedule = {
  /** Loan principal financed (homePrice − downPayment). */
  loanAmount: number;
  /** Fixed monthly principal-and-interest payment (dollars, 2dp). */
  monthlyPayment: number;
  /** Total number of monthly payments (termYears × 12). */
  months: number;
  /** Sum of interest paid across the full schedule (dollars, 2dp). */
  totalInterest: number;
  /** Per-month schedule. Length = months. */
  schedule: AmortizationMonth[];
};

/**
 * Compute a full mortgage amortization schedule.
 *
 * Rounding conventions (match consumer mortgage calculators):
 * - Monthly payment is rounded to the nearest cent (half away from zero).
 * - Interest each month is `round(balance × monthly_rate)` at cent precision.
 * - Principal is `monthlyPayment − interest`, so each row's numbers sum
 *   exactly to the payment.
 * - The final month's principal pays off whatever balance remains, so the
 *   schedule closes at $0.00 regardless of accumulated rounding drift.
 *
 * Edge cases:
 * - Zero interest: monthly payment is `loanAmount / months`, with the final
 *   month absorbing any rounding remainder.
 * - Zero or negative loan (100% cash purchase): returns an empty schedule
 *   with `monthlyPayment = 0` and `totalInterest = 0`.
 */
export function amortizeMortgage(inputs: MortgageInputs): MortgageSchedule {
  const { homePrice, downPayment, annualInterestRatePercent, termYears } = inputs;

  const loanAmount = roundCents(homePrice - downPayment);
  const months = Math.round(termYears * MONTHS_PER_YEAR);
  const monthlyRate =
    annualInterestRatePercent / PERCENT_DIVISOR / MONTHS_PER_YEAR;

  if (loanAmount <= 0 || months <= 0) {
    return {
      loanAmount: Math.max(0, loanAmount),
      monthlyPayment: 0,
      months: Math.max(0, months),
      totalInterest: 0,
      schedule: [],
    };
  }

  const monthlyPayment = computeMonthlyPayment(loanAmount, monthlyRate, months);

  const schedule: AmortizationMonth[] = [];
  let balance = loanAmount;
  let totalInterest = 0;

  for (let month = 1; month <= months; month++) {
    const interest = roundCents(balance * monthlyRate);
    let principal: number;
    let newBalance: number;

    if (month === months) {
      // Last row absorbs rounding drift so the schedule closes to $0.00.
      principal = balance;
      newBalance = 0;
    } else {
      principal = roundCents(monthlyPayment - interest);
      newBalance = roundCents(balance - principal);
    }

    totalInterest = roundCents(totalInterest + interest);
    schedule.push({ month, interest, principal, balance: newBalance });
    balance = newBalance;
  }

  return {
    loanAmount,
    monthlyPayment,
    months,
    totalInterest,
    schedule,
  };
}

function computeMonthlyPayment(
  loanAmount: number,
  monthlyRate: number,
  months: number,
): number {
  if (monthlyRate === 0) {
    return roundCents(loanAmount / months);
  }
  const growth = Math.pow(1 + monthlyRate, months);
  const payment = (loanAmount * monthlyRate * growth) / (growth - 1);
  return roundCents(payment);
}

/** Round to the nearest cent using half-away-from-zero (US bank convention). */
function roundCents(dollars: number): number {
  const cents = dollars * CENTS_PER_DOLLAR;
  const rounded =
    cents >= 0 ? Math.floor(cents + 0.5) : -Math.floor(-cents + 0.5);
  return rounded / CENTS_PER_DOLLAR;
}
